import type { MessageKey } from "@erp/i18n";
import type { IntegrationProvider, IntegrationUpdateData } from "@erp/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, CheckCircle2, CircleDashed, Copy, PlugZap, Webhook } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Field } from "~/components/ui/field";
import { Input } from "~/components/ui/input";
import { Panel } from "~/components/ui/page";
import { ErrorState, Skeleton } from "~/components/ui/states";
import { api, unwrap } from "~/lib/api";
import { errorMessageKey } from "~/lib/errors";
import { useFormat, useT } from "~/lib/i18n";
import { useCan, useSession } from "~/lib/session";
import { HelpTip } from "~/components/ui/help-tip";
import { ShopifySyncButton } from "~/features/settings/shopify-sync";

export const Route = createFileRoute("/_app/settings/integrations")({ component: IntegrationsPage });

const fetchIntegrations = () => unwrap(api.integrations.get());
type Data = Awaited<ReturnType<typeof fetchIntegrations>>;
type View = Data["integrations"][number];
type FieldView = View["fields"][number];

/**
 * Field labels follow `integrations.fields.<provider>.<key>`; every key listed in
 * INTEGRATION_FIELDS has its entry in both catalogs (checked by integration-keys.test.ts).
 */
const fieldKey = (provider: string, key: string, part: "label" | "hint") =>
  `integrations.fields.${provider}.${key}.${part}` as MessageKey;
const providerKey = (provider: IntegrationProvider, part: "name" | "description" | "howTo") =>
  `integrations.providers.${provider}.${part}` as const;

function IntegrationsPage() {
  const query = useQuery({ queryKey: ["integrations", "all"], queryFn: fetchIntegrations });
  if (query.isPending) return <Skeleton className="h-[480px] w-full" />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  return (
    <div className="flex flex-col gap-4">
      {query.data.integrations.map((view) => (
        <IntegrationCard key={view.provider} view={view} shopify={query.data.shopify} />
      ))}
    </div>
  );
}

type Draft = { values: Record<string, string | boolean>; secrets: Record<string, string | null> };

/** Only values saved from this screen prefill the inputs; server defaults show as placeholders. */
function initialValues(view: View): Draft["values"] {
  return Object.fromEntries(
    view.fields
      .filter((f) => f.kind !== "secret")
      .map((f) => [f.key, f.kind === "boolean" ? f.value === true : f.source === "saved" ? String(f.value ?? "") : ""]),
  );
}

function IntegrationCard({ view, shopify }: { view: View; shopify: Data["shopify"] }) {
  const t = useT();
  const queryClient = useQueryClient();
  const me = useSession();
  const provider = view.provider as IntegrationProvider;
  const [draft, setDraft] = useState<Draft>(() => ({ values: initialValues(view), secrets: {} }));
  const [baseline, setBaseline] = useState(view);
  const [showHelp, setShowHelp] = useState(false);
  const [locations, setLocations] = useState<{ id: string; name: string }[]>([]);
  const [sendTestEmail, setSendTestEmail] = useState(false);

  // A save or refetch replaces the view: start again from what the server now holds.
  if (baseline !== view) {
    setBaseline(view);
    setDraft({ values: initialValues(view), secrets: {} });
  }

  const initial = initialValues(view);
  const changedValues = Object.fromEntries(Object.entries(draft.values).filter(([k, v]) => v !== initial[k]));
  const pendingSecrets = Object.fromEntries(Object.entries(draft.secrets).filter(([, v]) => v === null || v.trim() !== ""));
  const dirty = Object.keys(changedValues).length > 0 || Object.keys(pendingSecrets).length > 0;

  const save = useMutation({
    mutationFn: () => {
      const payload: IntegrationUpdateData = {
        values: Object.fromEntries(Object.entries(changedValues).map(([k, v]) => [k, v === "" ? null : v])),
        secrets: pendingSecrets,
      };
      return unwrap(api.integrations({ provider }).put(payload));
    },
    onSuccess: () => {
      toast.success(t("integrations.saved"));
      void queryClient.invalidateQueries({ queryKey: ["integrations"] });
      if (provider === "inqom") void queryClient.invalidateQueries({ queryKey: ["settings"] });
    },
    onError: (error) => toast.error(t(errorMessageKey(error))),
  });

  const test = useMutation({
    mutationFn: () =>
      unwrap(api.integrations({ provider }).test.post({ to: provider === "email" && sendTestEmail ? me.email : undefined })),
    onSuccess: (result) => {
      toast.success(result.detail ? t("integrations.testOkDetail", { detail: result.detail }) : t("integrations.testOk"));
      setLocations(result.locations);
    },
    onError: (error) => toast.error(t(errorMessageKey(error))),
  });

  const setValue = (key: string, value: string | boolean) => setDraft((d) => ({ ...d, values: { ...d.values, [key]: value } }));
  const setSecret = (key: string, value: string | null | undefined) =>
    setDraft((d) => {
      const secrets = { ...d.secrets };
      if (value === undefined) delete secrets[key];
      else secrets[key] = value;
      return { ...d, secrets };
    });

  return (
    <Panel>
      <div className="flex flex-col gap-5 px-5 py-5 md:px-6">
        <header className="flex items-start gap-3">
          {view.configured ? (
            <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-success" aria-hidden />
          ) : (
            <CircleDashed className="mt-0.5 size-5 shrink-0 text-fg-3" aria-hidden />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="inline-flex items-center gap-0.5">
                <h2 className="font-display text-[16px] font-medium">{t(providerKey(provider, "name"))}</h2>
                {provider === "chorus" ? <HelpTip id="accounting.chorus" /> : null}
                {provider === "inqom" ? <HelpTip id="accounting.inqom" /> : null}
              </span>
              <Badge tone={view.configured ? "success" : "neutral"}>
                {view.configured ? t("integrations.active") : t("integrations.missing")}
              </Badge>
            </div>
            <p className="mt-0.5 text-sm text-fg-2">{t(providerKey(provider, "description"))}</p>
            <button
              type="button"
              aria-expanded={showHelp}
              onClick={() => setShowHelp((v) => !v)}
              className="mt-1.5 min-h-8 text-sm text-fg-2 underline underline-offset-4 hover:text-fg max-md:min-h-11"
            >
              {t("integrations.howTo")}
            </button>
            {showHelp ? (
              <p className="mt-1 rounded-control bg-surface-2 px-3 py-2.5 text-sm leading-relaxed text-fg-2">
                {t(providerKey(provider, "howTo"))}
              </p>
            ) : null}
          </div>
        </header>

        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <div className="grid gap-4 md:grid-cols-2">
            {view.fields.map((field) =>
              field.kind === "boolean" ? (
                <BooleanField
                  key={field.key}
                  provider={provider}
                  field={field}
                  checked={draft.values[field.key] === true}
                  onChange={(v) => setValue(field.key, v)}
                />
              ) : field.kind === "secret" ? (
                <SecretField
                  key={field.key}
                  provider={provider}
                  field={field}
                  draft={draft.secrets[field.key]}
                  onChange={(v) => setSecret(field.key, v)}
                />
              ) : (
                <TextField
                  key={field.key}
                  provider={provider}
                  field={field}
                  value={String(draft.values[field.key] ?? "")}
                  onChange={(v) => setValue(field.key, v)}
                  locations={field.key === "locationId" ? locations : []}
                />
              ),
            )}
          </div>

          {provider === "email" && view.configured ? (
            <label className="flex min-h-11 items-center gap-2.5 text-sm">
              <input
                type="checkbox"
                checked={sendTestEmail}
                onChange={(e) => setSendTestEmail(e.target.checked)}
                className="size-4 accent-[var(--brand-gold)] max-md:size-5"
              />
              {t("integrations.testEmailTo", { email: me.email })}
            </label>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" variant="primary" disabled={!dirty} loading={save.isPending}>
              {t("integrations.save")}
            </Button>
            <span className="inline-flex items-center gap-0.5">
              <Button type="button" onClick={() => test.mutate()} disabled={!view.configured || dirty} loading={test.isPending}>
                <PlugZap />
                {t("integrations.test")}
              </Button>
              <HelpTip id="settings.integrationTest" />
            </span>
            {dirty ? <span className="text-xs text-fg-3">{t("integrations.unsaved")}</span> : null}
          </div>
        </form>

        {provider === "shopify" ? <ShopifyTools configured={view.configured} shopify={shopify} /> : null}
      </div>
    </Panel>
  );
}

function hintFor(t: ReturnType<typeof useT>, provider: string, field: FieldView) {
  const hint = t(fieldKey(provider, field.key, "hint"));
  if (field.source === "server") return [hint, t("integrations.fromServer")].filter(Boolean).join(" — ");
  return hint || undefined;
}

function TextField({
  provider,
  field,
  value,
  onChange,
  locations,
}: {
  provider: string;
  field: FieldView;
  value: string;
  onChange: (value: string) => void;
  locations: { id: string; name: string }[];
}) {
  const t = useT();
  const serverValue = field.source === "server" && typeof field.value === "string" ? field.value : undefined;
  return (
    <div className="flex flex-col gap-2">
      <Field
        label={t(fieldKey(provider, field.key, "label"))}
        hint={hintFor(t, provider, field)}
        optionalLabel={field.required ? undefined : t("integrations.optional")}
      >
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={serverValue ?? placeholderOf(provider, field.key)}
          type={field.kind === "email" ? "email" : field.kind === "url" ? "url" : "text"}
          inputMode={field.kind === "number" ? "numeric" : field.kind === "email" ? "email" : undefined}
          autoComplete="off"
          spellCheck={false}
        />
      </Field>
      {locations.length ? (
        <div className="flex flex-col gap-1">
          <p className="text-xs text-fg-3">{t("integrations.locations")}</p>
          <ul className="flex flex-wrap gap-2">
            {locations.map((l) => (
              <li key={l.id}>
                <Button type="button" size="sm" variant={value === l.id ? "primary" : "secondary"} onClick={() => onChange(l.id)}>
                  {l.name}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function BooleanField({
  provider,
  field,
  checked,
  onChange,
}: {
  provider: string;
  field: FieldView;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  const t = useT();
  const id = `${provider}-${field.key}`;
  const hint = hintFor(t, provider, field);
  return (
    <div className="flex items-start gap-2.5 self-end text-sm md:min-h-[62px]">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        aria-describedby={hint ? `${id}-hint` : undefined}
        className="mt-0.5 size-4 accent-[var(--brand-gold)] max-md:size-5"
      />
      <div>
        <span className="flex items-center gap-0.5">
          <label htmlFor={id} className="block cursor-pointer font-medium">
            {t(fieldKey(provider, field.key, "label"))}
          </label>
          {provider === "shopify" && field.key === "writeBack" ? <HelpTip id="shopify.writeBack" /> : null}
          {provider === "inqom" && field.key === "sandbox" ? <HelpTip id="accounting.inqomSandbox" /> : null}
        </span>
        {hint ? (
          <p id={`${id}-hint`} className="text-xs text-fg-3">
            {hint}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/**
 * A stored secret is never sent back: the field shows whether one exists and offers to
 * replace or remove it. `draft` is undefined (keep), a string (replace) or null (remove).
 */
function SecretField({
  provider,
  field,
  draft,
  onChange,
}: {
  provider: string;
  field: FieldView;
  draft: string | null | undefined;
  onChange: (value: string | null | undefined) => void;
}) {
  const t = useT();
  const label = t(fieldKey(provider, field.key, "label"));
  const optionalLabel = field.required ? undefined : t("integrations.optional");
  const editing = typeof draft === "string" || (!field.isSet && draft !== null);
  if (!editing) {
    return (
      <Field label={label} hint={hintFor(t, provider, field)} optionalLabel={optionalLabel}>
        <div className="flex min-h-9 flex-wrap items-center gap-2 max-md:min-h-11">
          <Badge tone={draft === null ? "warning" : "success"}>
            {draft === null ? t("integrations.secretEmpty") : t("integrations.secretSet")}
          </Badge>
          {draft === null ? (
            <Button type="button" size="sm" variant="ghost" onClick={() => onChange(undefined)}>
              {t("integrations.secretKeep")}
            </Button>
          ) : (
            <>
              <Button type="button" size="sm" onClick={() => onChange("")}>
                {t("integrations.secretReplace")}
              </Button>
              {field.source === "saved" ? (
                <Button type="button" size="sm" variant="ghost" onClick={() => onChange(null)}>
                  {t("integrations.secretRemove")}
                </Button>
              ) : null}
            </>
          )}
        </div>
      </Field>
    );
  }
  return (
    <div className="flex flex-col gap-1.5">
      <Field label={label} hint={hintFor(t, provider, field)} optionalLabel={optionalLabel}>
        <Input
          type="password"
          value={draft ?? ""}
          onChange={(e) => onChange(field.isSet || e.target.value ? e.target.value : undefined)}
          placeholder={placeholderOf(provider, field.key)}
          autoComplete="new-password"
          spellCheck={false}
        />
      </Field>
      {field.isSet ? (
        <Button type="button" size="sm" variant="ghost" className="self-start" onClick={() => onChange(undefined)}>
          {t("integrations.secretKeep")}
        </Button>
      ) : null}
    </div>
  );
}

/** Technical examples (domains, token prefixes) read the same in every language. */
const EXAMPLES: Record<string, string> = {
  "shopify.shopDomain": "phygiatech.myshopify.com",
  "shopify.adminToken": "shpat_…",
  "shopify.apiVersion": "2026-07",
  "shopify.publicUrl": "https://phygiatech.com",
  "email.host": "smtp.gmail.com",
  "email.port": "465",
  "email.fromName": "Phygiatech",
  "email.fromEmail": "contact@phygiatech.com",
};
const placeholderOf = (provider: string, key: string) => EXAMPLES[`${provider}.${key}`];

function ShopifyTools({ configured, shopify }: { configured: boolean; shopify: Data["shopify"] }) {
  const t = useT();
  const format = useFormat();
  const can = useCan();

  const webhooks = useMutation({
    mutationFn: () => unwrap(api.integrations.shopify.webhooks.post()),
    onSuccess: (result) => toast.success(t("integrations.webhooksRegistered", { created: result.created })),
    onError: (error) => toast.error(t(errorMessageKey(error))),
  });

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shopify.webhookUrl);
      toast.success(t("integrations.copied"));
    } catch {
      toast.error(t("errors.generic"));
    }
  };

  return (
    <div className="flex flex-col gap-3 border-t border-line pt-4">
      <div className="flex flex-col gap-1.5">
        <p className="text-[13px] font-medium">{t("integrations.webhookUrl")}</p>
        <div className="flex items-center gap-2">
          <code className="min-w-0 flex-1 truncate rounded-control bg-surface-2 px-3 py-2 text-xs tabular">
            {shopify.webhookUrl}
          </code>
          <Button type="button" size="icon" variant="ghost" aria-label={t("integrations.copy")} onClick={() => void copy()}>
            <Copy />
          </Button>
        </div>
      </div>
      {shopify.failedWebhooks ? (
        <p className="flex items-center gap-1.5 text-xs font-medium text-danger">
          <AlertTriangle className="size-3.5" aria-hidden />
          {t("integrations.failedWebhooks", { count: shopify.failedWebhooks })}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-0.5">
          <Button type="button" onClick={() => webhooks.mutate()} disabled={!configured} loading={webhooks.isPending}>
            <Webhook />
            {t("integrations.registerWebhooks")}
          </Button>
          <HelpTip id="shopify.webhooks" />
        </span>
        {can("imports:run") ? <ShopifySyncButton disabled={!configured} /> : null}
        <span className="text-xs text-fg-3">
          {shopify.lastSyncAt
            ? t("integrations.lastSync", { date: format.relative(shopify.lastSyncAt) })
            : t("integrations.neverSynced")}
        </span>
      </div>
    </div>
  );
}
